/**
 * Drop a choice from THIS quote, with Undo (audit item 6, 2026-10-28).
 *
 * A per-device repair quote offers each failed device a few choices (Clean &
 * Flush, Rubber repair kit, Complete rebuild, Replacement). The office can
 * drop one from one device (the × on the choice), or drop one kind from every
 * device at once ("Drop Clean & Flush on all devices").
 *
 * Both use the remove-option endpoint, which deletes the choice and its lines
 * and hands back the rows exactly as stored. Undo sends those rows back to
 * `POST /estimates/:id/options/restore`, which puts the same rows back — same
 * prices, order, device and pick — without re-pricing anything.
 *
 * The customer's own repair settings are never touched. Rebuild brings every
 * choice back (keeping a removal through Rebuild is a later slice).
 */
import { useMutation } from "@tanstack/react-query";
import { toast } from "sonner";

import type { InfoTopic } from "@/components/info-button";
import { api } from "@/lib/api";
import type { DeviceGroupView } from "./option-groups";

export interface RemovedRows {
    options: Record<string, unknown>[];
    lines: Record<string, unknown>[];
}

interface RemoveResponse {
    ok: boolean;
    removedDecline?: boolean;
    removed?: RemovedRows;
}

/** What to call each kind of choice in the "Drop … on all devices" buttons. */
const KIND_NAME: Record<string, string> = {
    clean_flush: "Clean & Flush",
    rubber: "Rubber kit",
    total: "Complete rebuild",
    replace: "Replacement",
};

export interface DropEverywhereTarget {
    optionKey: string;
    /** Plain name for the button, e.g. "Clean & Flush". */
    name: string;
    /** One option per device that carries this kind of choice. */
    optionIds: string[];
}

/**
 * The "Drop … on all devices" buttons a quote should show: one per kind of
 * choice that two or more devices carry. One device is just the × on it.
 */
export function dropEverywhereTargets(groups: DeviceGroupView[] | null): DropEverywhereTarget[] {
    if (!groups || groups.length < 2) return [];
    const byKey = new Map<string, DropEverywhereTarget>();
    for (const g of groups) {
        for (const o of g.options) {
            if (o.isDecline) continue;
            const t = byKey.get(o.optionKey) ?? {
                optionKey: o.optionKey,
                name: KIND_NAME[o.optionKey] ?? o.label,
                optionIds: [],
            };
            t.optionIds.push(o.id);
            byKey.set(o.optionKey, t);
        }
    }
    const order = ["clean_flush", "rubber", "total", "replace"];
    return [...byKey.values()]
        .filter((t) => t.optionIds.length >= 2)
        .sort((a, b) => {
            const ai = order.indexOf(a.optionKey);
            const bi = order.indexOf(b.optionKey);
            return (ai < 0 ? 99 : ai) - (bi < 0 ? 99 : bi);
        });
}

function merge(into: RemovedRows, more: RemovedRows | undefined) {
    if (!more) return;
    into.options.push(...more.options);
    into.lines.push(...more.lines);
}

function errText(e: any): string {
    return e?.body?.message ?? e?.body?.error ?? e?.message ?? "unknown";
}

export function useOptionRemoval(opts: { estimateId: string; onChanged: () => void | Promise<void> }) {
    const { estimateId, onChanged } = opts;

    const restore = useMutation({
        mutationFn: (rows: RemovedRows) =>
            api.post<{ restoredOptions: number }>(`/api/estimates/${estimateId}/options/restore`, rows),
        onSuccess: async () => {
            await onChanged();
            toast.success("Put back on the quote");
        },
        onError: (e: any) => toast.error(`Undo failed: ${errText(e)}`),
    });

    const offerUndo = (message: string, rows: RemovedRows) => {
        toast.success(message, {
            duration: 12000,
            action: { label: "Undo", onClick: () => restore.mutate(rows) },
        });
    };

    const removeOne = useMutation({
        mutationFn: (v: { optionId: string; label: string }) =>
            api.delete<RemoveResponse>(`/api/estimates/${estimateId}/options/${v.optionId}`),
        onSuccess: async (r, v) => {
            await onChanged();
            const rows: RemovedRows = { options: [], lines: [] };
            merge(rows, r?.removed);
            const message = r?.removedDecline
                ? `Removed "${v.label}". It was the last choice, so this device is off the quote.`
                : `Removed "${v.label}" from this device.`;
            if (rows.options.length > 0) offerUndo(message, rows);
            else toast.success(message);
        },
        onError: (e: any) => toast.error(`Choice NOT removed: ${errText(e)}`),
    });

    const dropEverywhere = useMutation({
        mutationFn: async (t: DropEverywhereTarget) => {
            const rows: RemovedRows = { options: [], lines: [] };
            let done = 0;
            let firstError: unknown = null;
            // One device at a time: each remove re-picks that device's
            // pre-selected choice, and a failure part-way must still be undoable.
            for (const optionId of t.optionIds) {
                try {
                    const r = await api.delete<RemoveResponse>(`/api/estimates/${estimateId}/options/${optionId}`);
                    merge(rows, r?.removed);
                    done += 1;
                } catch (e) {
                    firstError ??= e;
                }
            }
            return { rows, done, total: t.optionIds.length, firstError };
        },
        onSuccess: async ({ rows, done, total, firstError }, t) => {
            await onChanged();
            if (done === 0) {
                toast.error(`${t.name} NOT removed: ${errText(firstError)}`);
                return;
            }
            const message =
                done === total
                    ? `Removed ${t.name} from ${done} devices.`
                    : `Removed ${t.name} from ${done} of ${total} devices. The rest didn't go through: ${errText(firstError)}`;
            if (rows.options.length > 0) offerUndo(message, rows);
            else toast.success(message);
        },
        onError: (e: any) => toast.error(`Choices NOT removed: ${errText(e)}`),
    });

    return {
        removeOne: (optionId: string, label: string) => removeOne.mutate({ optionId, label }),
        dropEverywhere: (t: DropEverywhereTarget) => dropEverywhere.mutate(t),
        pending: removeOne.isPending || dropEverywhere.isPending || restore.isPending,
    };
}

/** The ⓘ beside the × and the "Drop … on all devices" buttons. */
export const CHOICE_REMOVAL_HELP: InfoTopic = {
    title: "Removing a choice",
    lines: [
        "The × removes this choice from this quote only.",
        "The customer's settings don't change. Their next quote still gets every choice.",
        "Changed your mind? Press Undo on the message that pops up. It puts the choice back just as it was.",
        "Rebuild this quote (or Refresh pricing) brings removed choices back. It will warn you first.",
    ],
};
